import React, { useState, useEffect } from 'react';
import { db, auth, functions } from './firebase'; 
import { httpsCallable } from 'firebase/functions'; 
import { doc, setDoc, collection, addDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import * as Icons from 'react-icons/fa';

/**
 * ConnectAPI Component - Tesla Online Solution (ULTRA PRO VERSION)
 * High-End Enterprise UI with Coexistence (WhatsApp Business App) Enabled
 */
function ConnectAPI() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);
  
  // WhatsApp API Credentials
  const [phoneId, setPhoneId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  
  // Advanced Tracking States
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [metaData, setMetaData] = useState({ number: "", status: "", quality: "", tier: "" });
  const [isFetchingNumber, setIsFetchingNumber] = useState(false);
  
  // UI States
  const [isProcessing, setIsProcessing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 5000);
  };

  const logActivity = async (action, status, details = "") => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      await addDoc(collection(db, "businesses", user.uid, "api_logs"), {
        action, status, details, timestamp: serverTimestamp()
      });
    } catch (err) { console.error("Audit log failed"); }
  };

  // --- REAL-TIME DATABASE LISTENER ---
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsubscribe = onSnapshot(doc(db, "businesses", user.uid), (docSnap) => {
      if (docSnap.exists() && docSnap.data().phoneId && docSnap.data().accessToken) {
        const data = docSnap.data();
        if (phoneId !== data.phoneId) {
            fetchAdvancedMetaData(data.phoneId, data.accessToken);
        }
        setPhoneId(data.phoneId);
        setAccountId(data.accountId || "");
        setAccessToken(data.accessToken);
        setIsSetupComplete(true); 
      } else {
        setIsSetupComplete(false);
      }
    });
    return () => unsubscribe();
  }, [phoneId]);

  // --- 1. FACEBOOK SDK INITIALIZATION (UPGRADED TO v24.0) ---
  useEffect(() => {
    if (document.getElementById('facebook-jssdk')) { setIsSdkLoaded(true); return; }
    
    window.fbAsyncInit = function() {
      window.FB.init({
        appId      : '902227132534912',
        cookie     : true,
        xfbml      : true,
        version    : 'v24.0'
      });
      setIsSdkLoaded(true);
    };

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchAdvancedMetaData = async (pId, token) => {
    setIsFetchingNumber(true);
    try {
      const response = await fetch(`https://graph.facebook.com/v24.0/${pId}?fields=display_phone_number,quality_rating,status,name&access_token=${token}`);
      const data = await response.json();
      if (data.display_phone_number) {
        setMetaData({ 
          number: data.display_phone_number, 
          status: data.status || "CONNECTED", 
          quality: data.quality_rating || "GREEN",
          tier: "Tier 1 (1k/day)" 
        });
      } else {
        setMetaData(prev => ({ ...prev, number: "Invalid ID/Token" }));
      }
    } catch (error) { setMetaData(prev => ({ ...prev, number: "Network Sync Error" })); }
    setIsFetchingNumber(false);
  };

  // --- 2. THE MAGIC LOGIN FUNCTION (COEXISTENCE ENABLED) ---
  const launchEmbeddedSignup = () => {
    if (!window.FB) return showToast("Meta SDK still loading, please wait...", "error");
    setIsProcessing(true);

    window.FB.login((response) => {
      if (response.authResponse && response.authResponse.code) {
        const oauthCode = response.authResponse.code;
        
        const processLogin = async () => {
          try {
            const exchangeFunc = httpsCallable(functions, 'exchangeMetaCode');
            const result = await exchangeFunc({ oauthCode });
            if (result.data && result.data.success) {
              showToast("🎉 Connection Successful! Syncing Dashboard...", "success");
            }
          } catch (error) {
            showToast("Meta Setup failed on server.", "error");
          } finally {
            setIsProcessing(false);
          }
        };
        processLogin();
      } else {
        setIsProcessing(false);
        showToast("Login cancelled or interrupted.", "error");
      }
    }, {
      config_id: '28845257335060925',
      response_type: 'code',
      override_default_response_type: true,
      extras: { 
        sessionInfoVersion: '3',
        // ========================================================
        // MAGIC LINE: ALLOWS WHATSAPP BUSINESS APP COEXISTENCE
        // ========================================================
        featureType: 'whatsapp_business_app_onboarding' 
      }
    });
  };

  // --- 3. MANUAL DEVELOPER CONSOLE SAVE ---
  const handleSave = async (e) => {
    e.preventDefault();
    if (!/^\d+$/.test(phoneId.trim())) return showToast("Phone ID must be numbers only.", "error");
    setIsProcessing(true);
    try {
      await setDoc(doc(db, "businesses", auth.currentUser.uid), {
        phoneId: phoneId.trim(), accountId: accountId.trim(), accessToken: accessToken.trim(), updatedAt: new Date()
      }, { merge: true }); 
      showToast("API Credentials Saved!");
    } catch (error) { showToast("Database save failed.", "error"); }
    setIsProcessing(false);
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnecting will halt all automated messaging. Proceed?")) return;
    setIsProcessing(true);
    await setDoc(doc(db, "businesses", auth.currentUser.uid), { phoneId: "", accountId: "", accessToken: "" }, { merge: true });
    setIsProcessing(false);
  };

  // Enhanced Quality UI Helper
  const getQualityStyle = (quality) => {
    const q = quality?.toUpperCase() || "PENDING";
    if (q === "GREEN") return { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0', icon: <Icons.FaSignal />, label: "High Quality" };
    if (q === "YELLOW") return { bg: '#fffbeb', text: '#d97706', border: '#fde68a', icon: <Icons.FaExclamationTriangle />, label: "Medium Quality" };
    if (q === "RED") return { bg: '#fef2f2', text: '#dc2626', border: '#fecaca', icon: <Icons.FaTimesCircle />, label: "Low Quality (Flagged)" };
    return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1', icon: <Icons.FaHourglassHalf />, label: "Pending / Unknown" };
  };

  const isNumberValid = metaData.number && metaData.number !== "Invalid ID/Token" && metaData.number !== "Network Sync Error";

  // Inject Custom CSS Animations
  const customStyles = `
    @keyframes pulse-ring {
      0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
      70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
      100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
    }
    @keyframes float {
      0% { transform: translateY(0px); }
      50% { transform: translateY(-5px); }
      100% { transform: translateY(0px); }
    }
    .hover-card { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
    .hover-card:hover { transform: translateY(-4px); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04); }
    .glass-card {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 20px;
    }
  `;

  return (
    <div style={{ fontFamily: '"Inter", "Helvetica Neue", sans-serif', maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '15px' : '30px', background: '#f8fafc', minHeight: '100vh', borderRadius: '20px' }}>
      <style>{customStyles}</style>

      {/* TOAST NOTIFICATION */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', background: toast.type === 'error' ? 'linear-gradient(135deg, #ef4444, #b91c1c)' : 'linear-gradient(135deg, #10b981, #047857)', color: 'white', padding: '15px 25px', borderRadius: '12px', zIndex: 9999, fontWeight: '600', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '10px', animation: 'float 3s ease-in-out infinite' }}>
          {toast.type === 'success' ? <Icons.FaCheckCircle /> : <Icons.FaExclamationTriangle />}
          {toast.message}
        </div>
      )}

      {/* HEADER SECTION */}
      <div style={{ marginBottom: '35px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '20px' }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '28px' : '36px', fontWeight: '900', color: '#0f172a', letterSpacing: '-0.5px' }}>
            API Tracking Center
          </h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '15px', fontWeight: '500' }}>
            Enterprise WhatsApp Infrastructure Dashboard
          </p>
        </div>
      </div>

      {isSetupComplete ? (
        /* =========================================
           🟢 PRO TRACKING DASHBOARD (CONNECTED)
           ========================================= */
        <div style={{ animation: 'fadeIn 0.6s ease-out' }}>
          
          {/* TOP HERO BANNER */}
          <div style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', borderRadius: '24px', padding: isMobile ? '25px 20px' : '35px 40px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.4)', marginBottom: '30px', flexWrap: 'wrap', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ background: 'rgba(255,255,255,0.2)', padding: '16px', borderRadius: '16px', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.3)' }}>
                <Icons.FaWhatsapp style={{ fontSize: '36px', color: 'white' }} />
              </div>
              <div>
                <h2 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '20px' : '24px', fontWeight: '800', letterSpacing: '0.5px' }}>Connection Live & Routing</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: '700', background: 'rgba(255,255,255,0.15)', padding: '6px 14px', borderRadius: '20px', width: 'fit-content', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <div style={{ width: '8px', height: '8px', background: '#6ee7b7', borderRadius: '50%', animation: 'pulse-ring 2s infinite' }}></div>
                  System Fully Operational
                </div>
              </div>
            </div>
            <button onClick={() => fetchAdvancedMetaData(phoneId, accessToken)} disabled={isFetchingNumber} style={{ background: '#ffffff', color: '#059669', border: 'none', padding: '12px 24px', borderRadius: '14px', fontSize: '14px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', transition: 'transform 0.2s', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }} className="hover-card">
              <Icons.FaSyncAlt className={isFetchingNumber ? "fa-spin" : ""} /> {isFetchingNumber ? "Syncing API..." : "Refresh Tracking"}
            </button>
          </div>

          {/* COLORFUL TRACKING GRID */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '25px', marginBottom: '35px' }}>
            
            {/* 1. Phone Card */}
            <div className="glass-card hover-card" style={{ padding: '30px', position: 'relative', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <div style={{ position: 'absolute', top: '-10px', right: '-15px', fontSize: '100px', color: '#f8fafc', zIndex: 0 }}><Icons.FaPhoneAlt /></div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <p style={{ margin: '0 0 10px 0', color: '#64748b', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.2px' }}>Connected Number</p>
                <h3 style={{ margin: 0, fontSize: '28px', fontWeight: '900', color: isNumberValid ? '#0f172a' : '#ef4444' }}>
                  {isFetchingNumber ? '...' : (metaData.number || "Unknown")}
                </h3>
                {isNumberValid && (
                  <div style={{ margin: '12px 0 0 0', display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ecfdf5', color: '#059669', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: '1px solid #a7f3d0' }}>
                    <Icons.FaCheckCircle /> Verified via Meta
                  </div>
                )}
              </div>
            </div>

            {/* 2. Enhanced Quality Rating Card */}
            <div className="glass-card hover-card" style={{ padding: '30px', position: 'relative', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' }}>
                <p style={{ margin: '0 0 12px 0', color: '#64748b', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.2px' }}>Phone Quality Rating</p>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{ 
                    background: getQualityStyle(metaData.quality).bg, 
                    color: getQualityStyle(metaData.quality).text, 
                    border: `1px solid ${getQualityStyle(metaData.quality).border}`,
                    padding: '12px 20px', 
                    borderRadius: '12px', 
                    fontSize: '16px', 
                    fontWeight: '800', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    width: 'fit-content' 
                  }}>
                    {getQualityStyle(metaData.quality).icon} 
                    {isFetchingNumber ? 'Fetching...' : getQualityStyle(metaData.quality).label}
                  </div>
                </div>

                <p style={{ margin: '15px 0 0 0', color: '#94a3b8', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icons.FaShieldAlt size={14} color="#cbd5e1" /> High quality prevents message blocking.
                </p>
              </div>
            </div>

            {/* 3. Security/ID Card */}
            <div className="glass-card hover-card" style={{ padding: '30px', position: 'relative', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <p style={{ margin: '0 0 10px 0', color: '#64748b', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.2px' }}>API Phone ID</p>
                <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '10px', fontSize: '18px', fontFamily: '"Fira Code", monospace', color: '#334155', fontWeight: '700', border: '1px solid #e2e8f0', letterSpacing: '1px' }}>
                  {isFetchingNumber ? '...' : phoneId}
                </div>
                <div style={{ margin: '12px 0 0 0', display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#eff6ff', color: '#2563eb', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: '1px solid #bfdbfe' }}>
                  <Icons.FaLock /> 256-bit Encrypted
                </div>
              </div>
            </div>
          </div>

          {/* DANGER ZONE */}
          <div style={{ background: '#ffffff', borderRadius: '20px', padding: isMobile ? '25px' : '30px', border: '1px solid #fecaca', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', boxShadow: '0 4px 6px -1px rgba(239,68,68,0.05)' }}>
             <div>
               <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Connection Management</h4>
               <p style={{ margin: 0, fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Disconnecting will pause all CRM automations, drips, and bot replies instantly.</p>
             </div>
             <button onClick={handleDisconnect} disabled={isProcessing} style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#ef4444', padding: '14px 28px', borderRadius: '12px', fontSize: '14px', fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }} className="hover-card">
               <Icons.FaPlug /> {isProcessing ? "Processing..." : "Disconnect API"}
             </button>
          </div>

        </div>
      ) : (
        /* =========================================
           🔵 SETUP STATE (VIBRANT SPLIT DESIGN)
           ========================================= */
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '30px', animation: 'fadeIn 0.5s ease-out' }}>
          
          {/* Main Direct Connect Box (Premium Gradient) */}
          <div style={{ flex: '1.5', background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', padding: '40px', borderRadius: '20px', color: 'white', boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.4)', position: 'relative', overflow: 'hidden' }}>
            <Icons.FaFacebook style={{ position: 'absolute', right: '-20px', bottom: '-20px', fontSize: '150px', opacity: '0.1' }} />
            
            <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '800', marginBottom: '15px', backdropFilter: 'blur(5px)' }}>
              ✨ Coexistence Enabled
            </div>
            
            <h3 style={{ fontSize: '26px', fontWeight: '800', margin: '0 0 15px 0', position: 'relative', zIndex: 1 }}>Express Connection</h3>
            <p style={{ color: '#e0e7ff', fontSize: '16px', marginBottom: '40px', lineHeight: '1.6', position: 'relative', zIndex: 1, maxWidth: '90%' }}>
              Link your existing WhatsApp Business App directly to Tesla CRM. <b>You will not lose access to the app on your phone.</b> No manual API keys required.
            </p>
            
            <button onClick={launchEmbeddedSignup} disabled={isProcessing || !isSdkLoaded} style={{ background: 'white', color: '#1e3a8a', border: 'none', padding: '16px 30px', borderRadius: '12px', fontSize: '16px', fontWeight: '800', cursor: (isProcessing || !isSdkLoaded) ? 'not-allowed' : 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.2)', transition: 'transform 0.2s', opacity: (isProcessing || !isSdkLoaded) ? 0.7 : 1 }} className="hover-card">
              {isProcessing ? "Connecting..." : !isSdkLoaded ? "Loading Meta Secure Environment..." : <><Icons.FaFacebook size={22} color="#1877f2" /> Continue with Facebook</>}
            </button>
          </div>

          {/* Manual Developer Console */}
          <div style={{ flex: '1', background: 'white', padding: '35px', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '25px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '2px solid #f1f5f9', paddingBottom: '15px' }}>
              <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: '8px' }}><Icons.FaServer color="#475569" /></div>
              Developer Console
            </h3>
            
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '800', color: '#334155', display: 'block', marginBottom: '8px' }}>Phone Number ID</label>
                <input type="text" value={phoneId} onChange={(e) => setPhoneId(e.target.value)} style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '2px solid #e2e8f0', fontSize: '14px', background: '#f8fafc', transition: 'border 0.2s', boxSizing: 'border-box' }} placeholder="e.g. 104593..." />
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '800', color: '#334155', display: 'block', marginBottom: '8px' }}>Business Account ID</label>
                <input type="text" value={accountId} onChange={(e) => setAccountId(e.target.value)} style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '2px solid #e2e8f0', fontSize: '14px', background: '#f8fafc', boxSizing: 'border-box' }} placeholder="e.g. 119845..." />
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '800', color: '#334155', display: 'block', marginBottom: '8px' }}>Permanent Access Token</label>
                <input type="password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '2px solid #e2e8f0', fontSize: '14px', background: '#f8fafc', boxSizing: 'border-box' }} placeholder="EAAP..." />
              </div>
              <button type="submit" disabled={isProcessing} style={{ background: '#0f172a', color: 'white', border: 'none', padding: '15px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', marginTop: '10px', fontSize: '15px', transition: 'transform 0.2s' }} className="hover-card">
                {isProcessing ? "Authenticating..." : "Inject API Keys"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConnectAPI;